import type { ComponentType } from "react";
import type { BindingCtx, DomainId, DomainRead, WriteOp } from "../../../state/editor/types";

/** Advanced mode's groups — our own, not iJewel's tabs (see sections/groups.ts). */
import type { GroupId } from "../sections/groups";
export type TabId = GroupId;

/**
 * A typed read/write connection from a row to a domain slice. `get` reads the
 * value (live cross-domain reads available via `read`); `set` produces the
 * WriteOp the store applies (optimistic patch + RPC + reconcile).
 */
export interface Binding<T> {
  domain: DomainId;
  get(read: DomainRead, ctx: BindingCtx): T;
  set(value: T, ctx: BindingCtx): WriteOp;
}

/**
 * Audit marker for a control the dead-control inventory proved does not reach
 * the engine — the field parses, round-trips through `get*` and never reaches a
 * uniform, a material or a render call. Purely informational: the row keeps
 * behaving exactly as it did, it just admits what it is, so the surface can be
 * triaged (keep / wire up / delete) without re-doing the archaeology.
 */
export interface InertTag {
  /** Chip text; default "no effect". Use "best-effort" or "dev only" where the
   *  control does something real, just not what its label promises. */
  label?: string;
  /** Hover text: what is missing, and where the working equivalent lives. */
  reason: string;
}

interface BaseRow {
  id: string;
  label: string;
  hint?: string;
  /**
   * Extra terms the search box should match. A `custom` row draws its own
   * headings and so carries `label: ""` — which made it unreachable by search
   * (typing the name of a control that was right there returned "No matching
   * controls"). List those inner headings here.
   */
  keywords?: string;
  /** Flags the row as verified-inert; renders a muted chip beside the label. */
  inert?: InertTag;
  /** Conditional render (e.g. autoRadius gating). */
  visible?: (read: DomainRead, ctx: BindingCtx) => boolean;
  /**
   * Grey the row out. Return a string to also PRINT it under the row: the
   * disabled shell is `pointer-events-none`, so a `hint` tooltip on a disabled
   * row can never be hovered and the control just reads as broken.
   */
  disabled?: (read: DomainRead, ctx: BindingCtx) => boolean | string;
}

export interface CheckboxRow extends BaseRow {
  kind: "checkbox";
  binding: Binding<boolean>;
}
export interface SliderRow extends BaseRow {
  kind: "slider";
  min: number;
  max: number;
  step: number;
  unit?: string;
  /** Override the readout text (e.g. "Auto", "∞"). */
  format?: (v: number) => string;
  binding: Binding<number>;
}
export interface NumberRow extends BaseRow {
  kind: "number";
  min?: number;
  max?: number;
  step?: number;
  binding: Binding<number>;
}
export interface ColorRow extends BaseRow {
  kind: "color";
  /** Allow clearing to null (e.g. background "None"). */
  clearable?: boolean;
  binding: Binding<string | null>;
}
export interface Vec3Row extends BaseRow {
  kind: "vec3";
  min: number;
  max: number;
  step: number;
  /** Axis labels (default X/Y/Z). */
  axes?: [string, string, string];
  binding: Binding<[number, number, number]>;
}
export interface DropdownRow extends BaseRow {
  kind: "dropdown";
  options:
    | { id: string; label: string }[]
    | ((read: DomainRead, ctx: BindingCtx) => { id: string; label: string }[]);
  binding: Binding<string>;
}
export interface TextRow extends BaseRow {
  kind: "text";
  placeholder?: string;
  binding: Binding<string>;
}
export interface ButtonRow extends BaseRow {
  /** Domains to re-read once a button's `run` resolves. */
  refreshDomains?: DomainId[];
  kind: "button";
  buttons: {
    label: string;
    icon?: string;
    variant?: "primary" | "default";
    run: (ctx: BindingCtx) => void | Promise<unknown>;
  }[];
}
/** Free text + an action button (URL loaders). */
export interface TextActionRow extends BaseRow {
  /** Domains to re-read once `run` resolves (see the note above NoteRow). */
  refreshDomains?: DomainId[];
  kind: "textAction";
  placeholder: string;
  action: string;
  run: (value: string, ctx: BindingCtx) => void | Promise<unknown>;
}
export interface MonitorRow extends BaseRow {
  kind: "monitor";
  /** Poll its domain at this cadence while mounted (ms). */
  pollMs?: number;
  domain: DomainId;
  read: (read: DomainRead, ctx: BindingCtx) => string;
}
/**
 * Domains to re-read after an action row's `run` resolves. Opt-in: an action
 * that changes state a SIBLING row reads must say so, or that row keeps
 * rendering the slice from before the click. Without this the v48 metal-env
 * reporting never updated — `needsMetalHdr` gates "Metal Rotation" and "Metal
 * HDR Brightness Cap" on `metalEnvActive`, but nothing re-read `scene` after
 * the Load rows ran, so the gate stayed on whatever was true at mount.
 */
export interface NoteRow extends BaseRow {
  kind: "note";
  text: string | ((read: DomainRead, ctx: BindingCtx) => string);
}
/** Escape hatch for the ~6 bespoke UIs (tree, timeline, library grid, …). */
export interface CustomRow extends BaseRow {
  kind: "custom";
  /** A single control (a dropdown…): draw `label` beside it like any other row.
   *  Otherwise a custom block draws its own labels and `label` is search-only. */
  inline?: boolean;
  domains?: DomainId[];
  component: ComponentType<{ ctx: BindingCtx; read: DomainRead }>;
}

export type RowDef =
  | CheckboxRow
  | SliderRow
  | NumberRow
  | ColorRow
  | Vec3Row
  | DropdownRow
  | TextRow
  | ButtonRow
  | TextActionRow
  | MonitorRow
  | NoteRow
  | CustomRow;

export interface SectionDef {
  id: string;
  title: string;
  rows: RowDef[];
  /** Flags the whole section (every row inert, or a best-effort stand-in). */
  inert?: InertTag;
  /** iJewel collapses everything but the first section by default. */
  defaultCollapsed?: boolean;
  /** Whole-section visibility (e.g. material editor only with a selection). */
  visible?: (read: DomainRead, ctx: BindingCtx) => boolean;
  /** Small colored dot when the section's effect is enabled. */
  enabledBadge?: Binding<boolean>;
  /** Extra domains beyond those inferred from row bindings (custom rows). */
  domains?: DomainId[];
  /**
   * What this section is for, revealed by the ⓘ in its header. A plain `title=`
   * tooltip is useless on touch and invisible to the keyboard, so this renders
   * as an inline strip instead.
   */
  info?: string;
  /**
   * Header kebab menu. Rendered ONLY when present: the reference shows a ⋮ on
   * its sections, but what is inside was never measured, and 69 identical dead
   * kebabs would be worse than the affordance where it means something.
   */
  menu?: { label: string; run: (ctx: BindingCtx) => void | Promise<unknown> }[];
  /**
   * Title to DISPLAY, when it differs from `title`.
   *
   * `title` is the string `scripts/check-ijewel-parity.mjs` matches against the
   * captured checklist, by exact equality. Where the live reference now shows a
   * different label than the capture recorded, the canonical spelling stays in
   * `title` and the current one goes here.
   */
  displayTitle?: string;
  /**
   * Set on experimental / debug / duplicate sections (the reason why). They
   * render only while "Show experimental" is on. Applied by sections/index.ts.
   */
  experimental?: string;
}

export interface TabDef {
  id: TabId;
  sections: SectionDef[];
}

/** Domains a row touches (for the section's subscription set). */
function rowDomains(row: RowDef): DomainId[] {
  switch (row.kind) {
    case "checkbox":
    case "slider":
    case "number":
    case "color":
    case "vec3":
    case "dropdown":
    case "text":
      return [row.binding.domain];
    case "monitor":
      return [row.domain];
    case "custom":
      return row.domains ?? [];
    default:
      return [];
  }
}

/** Every domain a section needs subscribed (rows + badge + explicit + ui/selection). */
export function sectionDomains(section: SectionDef): DomainId[] {
  const set = new Set<DomainId>(["ui", "selection"]);
  for (const r of section.rows) for (const d of rowDomains(r)) set.add(d);
  if (section.enabledBadge) set.add(section.enabledBadge.domain);
  for (const d of section.domains ?? []) set.add(d);
  return [...set];
}
